import './mapboxControls.css';
import {useEffect} from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {atom, useSetAtom} from 'jotai';
import type {GeoJSONSource, Map as MapboxMap} from 'mapbox-gl';
import useMapStyle, {clickableLayers} from './useMapStyle';
import getBoundingBox from 'src/utils/map/getBoundingBox';
import useMapSelectedPlace from './useMapSelectedPlace';
import fetcher from 'src/utils/fetcher';

mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || '';

export type MapLoadingPlace = {
  isLoading: boolean;
  name: string;
  location: [number, number];
};

export const mapLoadingPlaceAtom = atom<MapLoadingPlace | null>(null);

type OnPlacePinClickProps = {
  places: Partial<Place>[];
  placeId: string;
  setSelectedPlace: (place: Partial<Place>) => void;
};

const onPlacePinClick = ({placeId, places, setSelectedPlace}: OnPlacePinClickProps) => {
  const place = places.find((p) => p._id === placeId);

  if (place) setSelectedPlace(place);
};

type OnClusterClickProps = {
  clusterId: number;
  map: MapboxMap;
};

const onClusterClick = ({map, clusterId}: OnClusterClickProps) => {
  const clusterSource = map.getSource('places') as GeoJSONSource;

  clusterSource.getClusterLeaves(clusterId, 1000, 0, (error, features) => {
    const coordinates: [number, number][] = features?.map((f: any) => f.geometry.coordinates).filter(Boolean) || [];
    const boundingBox = getBoundingBox(coordinates);

    map.fitBounds(boundingBox, {padding: 80});
  });
};

type OnMapLabelClickProps = {
  placeName: string;
  location: [number, number];
  setSelectedPlace: (place: Partial<Place>) => void;
  setLoadingPlace: (place: MapLoadingPlace | null) => void;
};

const onMapLabelClick = async ({placeName, location, setSelectedPlace, setLoadingPlace}: OnMapLabelClickProps) => {
  setLoadingPlace({name: placeName, location, isLoading: true});

  const place = await fetcher({
    url: '/api/places/location',
    query: {name: placeName, lat: location[1], lng: location[0]}
  }).catch(() => null);

  setLoadingPlace(null);

  if (place) setSelectedPlace(place);
  if (!place) setLoadingPlace({name: placeName, location, isLoading: false});
};

type Options = {
  variant: 'countries' | 'places';
  highlightedCountryCodes?: string[];
  places?: Partial<Place>[];
  themeOverride?: 'light' | 'dark';
  zoomOverride?: number;
  padding?: {top: number; right: number; bottom: number; left: number};
};

const useMap = ({variant, highlightedCountryCodes, places = [], themeOverride, zoomOverride, padding}: Options) => {
  const {mapContainer, map} = useMapStyle({
    variant,
    highlightedCountryCodes,
    places,
    themeOverride,
    zoomOverride,
    padding
  });

  useEffect(() => {
    if (!map) return;

    const onMapClick = (e: mapboxgl.MapMouseEvent) => {
      const feature = e.features?.[0];
      if (!feature) return;

      const placeId = feature.properties?.placeId;

      if (placeId) return onPlacePinClick({placeId, places, setSelectedPlace});

      const clusterId = feature.properties?.cluster_id;

      if (clusterId) return onClusterClick({clusterId, map});

      const geometry = feature.geometry as {type: string; coordinates: [number, number]};

      return onMapLabelClick({
        placeName: feature.properties?.name_en || feature.properties?.name,
        location: geometry?.coordinates,
        setSelectedPlace,
        setLoadingPlace
      });
    };

    map.on('click', clickableLayers, onMapClick);

    return () => {
      map.off('click', clickableLayers, onMapClick);
    };
  }, [map, places]);

  const setLoadingPlace = useSetAtom(mapLoadingPlaceAtom);

  const {setSelectedPlace} = useMapSelectedPlace({places, map});

  return {mapContainer, map};
};

export default useMap;
